import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "grid w-full h-full"
}
const _hoisted_2 = { class: "col sub-menu h-full" }
const _hoisted_3 = { class: "row-start" }
const _hoisted_4 = { class: "col border-top" }
const _hoisted_5 = { class: "flex-row gap-05 tooltip mtop-10 w-full align-center" }
const _hoisted_6 = ["src"]
const _hoisted_7 = { class: "font-m" }
const _hoisted_8 = { class: "tooltiptext max-w-500 mtop-15 font-s" }
const _hoisted_9 = { class: "col planing-agenda content-transparent w-full h-full" }
const _hoisted_10 = { class: "col planning-sidebar gap-15 max-w-250" }
const _hoisted_11 = {
  key: 1,
  class: "w-full h-full error rowcenter"
}

import esCalendar from "@/components/calendar/esCalendar.vue";
import {computed, onMounted, Ref, ref, watch} from 'vue';
import DateService from "@/services/DateService";
import {addDays, isToday, isEqual, format} from "date-fns";
import {CalendarView, codeErrorNoPlanning, defaultLocal, enLocal} from '@/models/calendar.model';
import {ApiError, DateFormat, ItemTag } from '@/models/share.model';
import EsPlanningWaitingRooms from "@/components/planning/esPlanningWaitingRooms.vue";
import EsPlanningHistory from "@/components/planning/esPlanningHistory.vue";
import EsWithoutAppointment from "@/components/planning/esWithoutAppointment.vue";
import { usePermissionsStore } from "@/stores/permissionsStore";
import router from '@/router'
import { useAppUserStore } from "@/stores/appUser";
import { useI18n } from "vue-i18n";
import SharedService from "@/services/SharedService";
import { toastError, toastWarn } from '@/utils/toast-utils';
import { useToast } from "primevue";
import { PlanningApi } from "@/apiGenerate/medserver";
import { medServerApiConfig } from "@/config/medserver-api.config";
import { PlanningRefreshSource } from "@/views/planningMain/enumPlanningRefreshSource";
import { useCurrentEntryStore } from "@/stores/currentEntry";


export default /*@__PURE__*/_defineComponent({
  __name: 'esPlanningMain',
  setup(__props) {

const permissionsStore = usePermissionsStore();
const appUserStore = useAppUserStore();
const {t} = useI18n();
const toast = useToast();
const currentEntryStore = useCurrentEntryStore();

const planningApi = new PlanningApi(medServerApiConfig);

const doctors = ref<ItemTag[]>([]);
const selectedDoctors = ref<ItemTag[]>([]);
const lastSelectedDoctorId = ref<string>(undefined);
const doctorLoading = ref<boolean>(true);
const currentView = appUserStore.getCurrentAgendaType() ? (Number.parseInt(appUserStore.getCurrentAgendaType()) === 1 ? ref(CalendarView.WEEK) : ref(CalendarView.DAY)) : ref(CalendarView.WEEK);
const selectedDate = appUserStore.getCurrentAgendaDate() ? ref<Date>(new Date(appUserStore.getCurrentAgendaDate())) : ref<Date>(new Date());
const datePicker = ref(null);
const refresh = ref<boolean>(false);

const lastDoctorOpened = ref<ItemTag>(null);
const disabledDates = ref([]);

// variables dedicated to detect the sources of the refresh to apply proper refresh and scroll logic
const changeDate = ref<string>('');
const changeAgendaType = ref<string>('');
const changeDoctor = ref<string>('');
const changeDoctorList = ref<string>('');

const localeForVCalendar = computed(() => {
  return appUserStore.currentAppUserData?.preferences.menuLanguageIso || defaultLocal;
});

const attrs = ref([
  {
    key: 'today',
    highlight: {
        class: 'background-today',
    },
    dates: new Date(),
  },
  {
    highlight: {
      start: { fillMode: 'light' },
      base: { fillMode: 'light' },
      end: { fillMode: 'light' },
    },
    dates: { start: DateService.getFirstDayOfWeek(selectedDate.value, localeForVCalendar.value !== enLocal), end: DateService.getLastDayOfWeek(selectedDate.value, localeForVCalendar.value !== enLocal) },
  }
]);

//method dedicated to change the refresh variables if we press the same button multiple times
const updateRefreshAutoScrollVariable = (refreshVariable : Ref<string, string>, baseRefreshValue: string) => {
  if (refreshVariable.value !== baseRefreshValue) {
    refreshVariable.value = baseRefreshValue; 
  } else {
    refreshVariable.value = ' ' + baseRefreshValue; 
  }
}

const currInstituteUuid = computed(() => {
  if (!appUserStore.currentHealthGroupMembership) {return "";}
  return appUserStore.getCurrentInstituteWithRole().instituteWithRoleId;
});

const currInstituteName = computed(() => {
  if (!appUserStore.currentHealthGroupMembership) {return "";}
  return appUserStore.getCurrentInstituteWithRole().instituteName;
});


const updateSelectedDate = (newDate: Date, moveCalendar: boolean) => {
    selectedDate.value = newDate;
    
    if (moveCalendar && datePicker.value) {
      datePicker.value.move(newDate);
    }
    
    appUserStore.updateCurrentAgendaDate(format(selectedDate.value, DateFormat.ISO_SMALL));

    attrs.value = [
      {
        key: 'selectedDate',
        highlight: {
            class: isToday(newDate) ? 'background-today' : 'background-selected'
        },
        dates: newDate,
      },
      {
        key: 'today',
        highlight: {
          class:  isEqual(new Date(), new Date(newDate)) ? isToday(newDate) ? 'background-today' : 'background-selected' : 'background-today-unselected'
        },
        dates: new Date(),
      },
      {
        highlight: {
          start: { fillMode: 'light'},
          base: { fillMode: 'light'},
          end: { fillMode: 'light' }
        },
        dates: { start: DateService.getFirstDayOfWeek(newDate, localeForVCalendar.value !== enLocal), end: DateService.getLastDayOfWeek(newDate, localeForVCalendar.value !== enLocal) },
      }
    ];
    setRefresh({newValue: true, newValueCalendar: true});
}

const getHealthProByInstitute = async () => {
  if (currInstituteUuid.value) {
    doctorLoading.value = true;

    try {
      const result = await planningApi.getPlanningHealthProList({ instituteId: currInstituteUuid.value });
      doctors.value = result.map((planningPermission) => ({
        id: planningPermission.healthproId,
        label: planningPermission.name,
        selected: true,
        opened: false
      }));

      const sessionStorageDoctors: ItemTag[] = appUserStore.getCurrentAgendaHealthPro();

      if (currentEntryStore.moveAppointmentFromPatient?.healthProId) {
        doctors.value.forEach((doctor: ItemTag) => {
          doctor.selected = doctor.id === currentEntryStore.moveAppointmentFromPatient.healthProId;
          doctor.opened = doctor.id === currentEntryStore.moveAppointmentFromPatient.healthProId;
        });
        updateSelectedDoctors();
      } else if (sessionStorageDoctors?.length > 0) {
        selectedDoctors.value = sessionStorageDoctors;
        doctors.value = sessionStorageDoctors;
      } else {
        doctors.value[0].opened = true;
        updateSelectedDoctors();
      }

      setRefresh({newValue: true, newValueCalendar: true});
    } catch (e: unknown) {
      doctors.value = [];
      const error = e as ApiError;
      if (error.status != codeErrorNoPlanning) {
        toastError(toast, {
          summary: t('common.toast.error'),
          detail: error.status ? t('error.' + error.status) : t('common.toast.error')
        });
      }
    } finally {
      doctorLoading.value = false;
    }
  }
}

const updateSelectedDoctors = () => {
    appUserStore.updateCurrentAgendaHealthPro(doctors.value);
    selectedDoctors.value = doctors.value;
}

const previous = (newDate: Date) => {  
  updateRefreshAutoScrollVariable(changeDate, PlanningRefreshSource.LeftDateChange);
  
  if (currentView.value == CalendarView.WEEK) {
    updateSelectedDate(addDays(newDate, -7), true);
  } else {
    updateSelectedDate(addDays(newDate, -1), true);
  }
}

const next = (newDate: Date) => {
  updateRefreshAutoScrollVariable(changeDate, PlanningRefreshSource.RightDateChange);

  if (currentView.value == CalendarView.WEEK ) {
    updateSelectedDate(addDays(newDate, 7), true);
  } else {
    updateSelectedDate(addDays(newDate, 1), true);
  }
}

const today = (newDate: Date) => {
  updateRefreshAutoScrollVariable(changeDate, PlanningRefreshSource.TodayDateChange);
  updateSelectedDate(newDate, false);
}

const pickDate = (newDate: Date) => {
  updateRefreshAutoScrollVariable(changeDate, PlanningRefreshSource.TodayDateChange);
  updateSelectedDate(newDate, false);
}

const changeCurrentView = (newView: CalendarView) => {
  currentView.value = newView;

  if (currentView.value === CalendarView.DAY) {
    selectedDoctors.value.forEach((doctor: ItemTag) => doctor.opened = doctor.selected);
  } else {
    selectedDoctors.value.forEach((doctor: ItemTag) => doctor.opened = false);
    if (lastDoctorOpened.value) {
      const doctorToOpen: ItemTag = selectedDoctors.value.filter((doctor: ItemTag) => doctor.selected && doctor.id === lastDoctorOpened.value.id)[0];

      if (doctorToOpen) {
        doctorToOpen.opened = true;
      } else {
        selectedDoctors.value.filter((doctor) => doctor.selected)[0].opened = true;
      }
    } else {
      selectedDoctors.value.filter((doctor) => doctor.selected)[0].opened = true;
    }    
  }

  updateRefreshAutoScrollVariable(changeAgendaType, currentView.value);

  setRefresh({ newValue: true, newValueCalendar: true });
}

const tagClick = (item: ItemTag) => {
    const index = doctors.value.findIndex((doctor)=> doctor.id === item.id);
  
    updateRefreshAutoScrollVariable(changeDoctorList, PlanningRefreshSource.DoctorChangeList);

    doctors.value[index].selected = !doctors.value[index].selected;

    lastSelectedDoctorId.value = item.id;

    if (!doctors.value.find((doctor) => doctor.selected)) {
      toastWarn(toast, {
        summary: t('common.toast.warning'),
        detail:  t(`warn.ONE_DOCTOR_SELECTED`),
        life: 5000
      });
      doctors.value[index].selected = true;
    } else {
      doctors.value[index].opened = doctors.value[index].selected;

      let refreshCalendar = false;
      if (doctors.value[index].opened) {
        refreshCalendar = true;
        doctors.value.forEach((doctor) => {
          if (doctor.id !== item.id) { 
            doctor.opened = false;
          }
        });
      }

      if (!doctors.value.some((doctor) => !!doctor.opened)) {
        doctors.value.filter((doctor) =>  doctor.selected)[0].opened = true;
        refreshCalendar = true;
      }

      updateSelectedDoctors();     
      setRefresh({newValue: true, newValueCalendar: refreshCalendar});
    }
}

const switchOpened = (item: ItemTag) => {
    doctors.value.forEach((doctor) => doctor.opened = false);
    const index = doctors.value.findIndex((doctor)=> doctor.id === item.id);

    updateRefreshAutoScrollVariable(changeDoctor, PlanningRefreshSource.DoctorChange);

    doctors.value[index].opened = true;
    
    updateSelectedDoctors();
    setRefresh({newValue: true, newValueCalendar: true});
}

const saveLastDoctorOpened = (item: ItemTag) => {
  if (item) {
    const index = doctors.value.findIndex((doctor)=> doctor.id === item.id);
    if (index >= 0) {
      doctors.value[index].opened = true;
      lastDoctorOpened.value = doctors.value[index];
    }
  }  
}  

const updateDoctors = (newDoctors: ItemTag[]) => {
  if (newDoctors) {
    doctors.value = newDoctors;
  }
}

async function checkPermission() {
  const hasAccess = await permissionsStore.checkPermission("READ_PLANNING");
  
  if (!hasAccess) {
      router.push('/error?error=USER_FORBIDDEN');
  }
}

const setRefresh = (dataNewValue : {newValue: boolean, newValueCalendar: boolean}) => {
  refresh.value = dataNewValue.newValue;
  appUserStore.setRefreshCalendar(dataNewValue.newValueCalendar);
  appUserStore.setRefreshOnlyEvent(dataNewValue.newValue && !dataNewValue.newValueCalendar);
}

watch(
  () => currInstituteUuid.value,
  async () => {
    await getHealthProByInstitute();
  }
);

watch(
  () => appUserStore.refreshHealthProCalendar,
  async () => {
    if (appUserStore.refreshHealthProCalendar) {
      appUserStore.updateCurrentAgendaHealthPro(null);
      await getHealthProByInstitute();
      appUserStore.setRefreshHealthProCalendar(false);
    }
  }
);

onMounted(async () => {
    checkPermission();
    await getHealthProByInstitute();
    updateSelectedDate(selectedDate.value, true);
});

return (_ctx: any,_cache: any) => {
  const _component_VCalendar = _resolveComponent("VCalendar")!
  const _component_es_tag_list = _resolveComponent("es-tag-list")!

  return (doctorLoading.value || doctors.value.length > 0)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_component_VCalendar, {
              ref_key: "datePicker",
              ref: datePicker,
              "disabled-dates": disabledDates.value,
              rows: 2,
              step: 1,
              transparent: "",
              borderless: "",
              attributes: attrs.value,
              onDayclick: _cache[0] || (_cache[0] = ($event: any) => (pickDate($event.date))),
              "trim-weeks": "",
              locale: localeForVCalendar.value
            }, null, 8, ["disabled-dates", "attributes", "locale"])
          ]),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("img", {
                class: "item-m",
                src: _unref(SharedService).getIcon('building-2', true, 'shareIcons')
              }, null, 8, _hoisted_6),
              _createElementVNode("span", _hoisted_7, _toDisplayString(currInstituteName.value), 1),
              _createElementVNode("span", _hoisted_8, _toDisplayString(currInstituteName.value), 1)
            ]),
            _createVNode(_component_es_tag_list, {
              items: selectedDoctors.value,
              onTagClick: _cache[1] || (_cache[1] = ($event: any) => (tagClick($event))),
              style: 'mtop-10',
              displayTooltip: true,
              disabled: refresh.value
            }, null, 8, ["items", "disabled"])
          ])
        ]),
        _createElementVNode("div", _hoisted_9, [
          _createVNode(esCalendar, {
            "selected-date": _unref(selectedDate),
            "current-view": _unref(currentView),
            doctors: selectedDoctors.value,
            refresh: refresh.value,
            changeDate: changeDate.value,
            changeAgendaType: changeAgendaType.value+_unref(currentView),
            changeDoctor: changeDoctor.value,
            changeDoctorList: changeDoctorList.value,
            lastSelectedDoctorId: lastSelectedDoctorId.value,
            onToday: _cache[2] || (_cache[2] = ($event: any) => (today($event))),
            onRefreshCalendar: _cache[3] || (_cache[3] = ($event: any) => (refresh.value = true)),
            onUpdateSelectedDate: _cache[4] || (_cache[4] = ($event: any) => (updateSelectedDate($event, true))),
            onNext: _cache[5] || (_cache[5] = ($event: any) => (next($event))),
            onPrev: _cache[6] || (_cache[6] = ($event: any) => (previous($event))),
            onChangeCurrentView: _cache[7] || (_cache[7] = ($event: any) => (changeCurrentView($event))),
            onSwitchOpened: _cache[8] || (_cache[8] = ($event: any) => (switchOpened($event))),
            onSaveLastDoctorOpened: _cache[9] || (_cache[9] = ($event: any) => (saveLastDoctorOpened($event))),
            onRefreshed: _cache[10] || (_cache[10] = ($event: any) => (setRefresh($event))),
            onUpdateDoctors: _cache[11] || (_cache[11] = ($event: any) => (updateDoctors($event)))
          }, null, 8, ["selected-date", "current-view", "doctors", "refresh", "changeDate", "changeAgendaType", "changeDoctor", "changeDoctorList", "lastSelectedDoctorId"])
        ]),
        _createElementVNode("div", _hoisted_10, [
          _createVNode(EsWithoutAppointment, {
            doctors: selectedDoctors.value.filter((doctor) => doctor.selected),
            refresh: refresh.value,
            onRefreshCalendar: _cache[12] || (_cache[12] = ($event: any) => (refresh.value = true))
          }, null, 8, ["doctors", "refresh"]),
          _createVNode(EsPlanningWaitingRooms, {
            onRefreshCalendar: _cache[13] || (_cache[13] = ($event: any) => (refresh.value = true))
          }),
          _createVNode(EsPlanningHistory, { "selected-date": _unref(selectedDate) }, null, 8, ["selected-date"])
        ])
      ]))
    : (_openBlock(), _createElementBlock("div", _hoisted_11, [
        _createElementVNode("span", null, _toDisplayString(_unref(t)('calendar.label.notAccess')), 1)
      ]))
}
}

})